<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>Coach</h1>
    </div>
    <div class="border-container-unique37">
      <div class="admin-list-title">List of Coach</div>
      <div>You can view, accept, pending, or reject coach.</div>

      <div class="admin-flex-container" ref="adminFlexContainer">
        <div class="admin-search-container">
          <input
            type="text"
            class="admin-search-input"
            placeholder="Search..."
            v-model="searchQuery"
            @input="handleSearchChange"
          />
          <img
            src="/images/maker-cart/union.svg"
            alt="Search"
            class="admin-search-icon"
          />
        </div>
      </div>
      <div class="admin-list-tool-container"></div>

      <div class="mb-3" style="display: flex; gap: 10px; flex-wrap: wrap"></div>

      <TableData
        :headers="headers"
        :items="tableItems"
        :loading="tableLoading"
        :total-pages="data?.data?.total_pages || 0"
        :current-page="currentPage"
        @update:current-page="changePage"
        :is-action="false"
        :use-api-sort="true"
        @sort="handleTableSort"
        :customActions="[]"
        :show-data-info="true"
        :total-rows="data?.data?.total_rows || 0"
        :current-limit="filters.limit"
        variant="rounded"
      >
        <template #cell-information="{ item }">
          <div>Grade: {{ item.raw.grade }}</div>
          <div>Subject: {{ item.raw.subject }}</div>
          <div>Country: {{ item.raw.country }}</div>
          <div>
            Uploaded File:
            <a :href="item.raw.uploaded_file" target="_blank" rel="noopener"
              >Download</a
            >
          </div>
        </template>
        <template #cell-actions="{ item }">
          <div
            style="display: flex; flex-direction: column; align-items: center"
          >
            <button
              @click="updateStatus(item.id, 'Accepted')"
              class="button-coach button-coach-accepted"
            >
              Accepted
            </button>
            <button
              @click="updateStatus(item.id, 'Pending')"
              class="button-coach button-coach-pending"
            >
              Pending
            </button>
            <button
              @click="updateStatus(item.id, 'Rejected')"
              class="button-coach button-coach-rejected"
            >
              Rejected
            </button>
            <img
              class="cursor-pointer"
              @click="openModal(item.id)"
              src="/images/icon/subtract.svg"
              alt="delete"
            />
          </div>
        </template>
      </TableData>
    </div>
  </div>

  <ConfirmationModal
    v-model="showModalDelete"
    variant="danger"
    title="Remove Coach"
    message="Are you sure want to remove this coach?"
    confirm-text="Confirm"
    cancel-text="Back"
    :loading="isLoading"
    @confirm="remove(itemId)"
    @cancel="updateVisibilityModalDelete"
  />
</template>

<script setup>
/* eslint-disable */
import { onMounted, ref, computed, watch, inject } from "vue";
import coachService from "@/services/coachService";
import ConfirmationModal from "@/components/common/ConfirmationModal.vue";
import { debounce } from "lodash";
import TableData from "@/components/common/TableData.vue";
import { useUrlQuerySync } from "@/composeables/useUrlQuerySync";

const showModalDelete = ref(false);
const isLoading = ref(false);
const itemId = ref(null);
const tableLoading = ref(true);
const searchQuery = ref("");
const currentStatus = ref("");
const notify = inject("notify");

const data = ref({
  message: "",
  error: null,
  data: {
    search: "",
    page: 0,
    limit: 0,
    total_rows: 0,
    total_pages: 0,
    rows: [],
  },
});

const currentPage = ref(1);
const filters = ref({
  search: "",
  page: 1,
  limit: 10,
  sort_by: "date_added",
  order: "desc",
});

useUrlQuerySync({
  filters,
  excludeDefaults: [{ page: 1 }, { limit: 10 }],
});

onMounted(async () => {
  await getAll();
  window.addEventListener("resize", updateLayout);
  updateLayout();
});

// Define table headers
const headers = ref([
  { text: "User", value: "user", sortable: false },
  { text: "Date & Time", value: "date_time", sortable: false },
  { text: "Status", value: "status", sortable: true },
  { text: "Information", value: "information", sortable: false },
  { text: "Description", value: "description", sortable: false },
  { text: "Date Added", value: "date_added", sortable: true },
  { text: "Action", value: "actions", sortable: false },
]);

// Map API rows to table items
const tableItems = computed(() => {
  return (data.value?.data?.rows || []).map((item) => ({
    id: item.coach_id,
    user:
      `${item.user?.firstname || ""} ${item.user?.lastname || ""}`.trim() ||
      "-",
    date_time: `${item.date || ""} ${item.time || ""}`.trim(),
    status: item.status || "-",
    information: "",
    description: item.description || "-",
    date_added: item.date_added || "-",
    actions: "",
    raw: item,
  }));
});

const getAll = async () => {
  try {
    tableLoading.value = true;

    const response = await coachService.getAll(filters.value);
    if (response.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    console.log(error.message);
  } finally {
    tableLoading.value = false;
  }
};

// Sync search query with filters.search
watch(
  () => filters.value.search,
  (newValue) => {
    searchQuery.value = newValue || "";
  }
);

// Debounced search handler
const handleSearchChange = debounce(() => {
  filters.value.search = searchQuery.value;
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
}, 300);

const changePage = (page) => {
  currentPage.value = page;
  filters.value.page = page;
  getAll();
};

const prevPage = () => {
  if (currentPage.value > 1) {
    changePage(currentPage.value - 1);
  }
};

const nextPage = () => {
  if (currentPage.value < data.value.data.total_pages) {
    changePage(currentPage.value + 1);
  }
};

const visiblePages = computed(() => {
  const total = data.value.data.total_pages;
  const current = currentPage.value;
  const start = Math.max(1, current - 2);
  const end = Math.min(total, current + 2);

  const pages = [];
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
});

const updateVisibilityModalDelete = () => {
  showModalDelete.value = !showModalDelete.value;
};

// Function to update layout based on window width
const updateLayout = () => {
  const container = document.querySelector(".admin-flex-container");
  const toolContainer = document.querySelector(".admin-list-tool-container");
  const parentContainer = document.querySelector(".border-container-unique37");
  const width = window.innerWidth;

  if (width <= 991) {
    parentContainer.insertBefore(toolContainer, container.nextSibling);
  } else {
    container.appendChild(toolContainer);
    container.insertBefore(toolContainer, container.children[1]);
  }
};

const handleTableSort = ({ sort_by, sort_dir }) => {
  filters.value.sort_by = sort_by;
  filters.value.order = sort_dir.toLowerCase();
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
};

const updateStatus = async (id, status) => {
  isLoading.value = true;
  try {
    await coachService.updateStatus(id, status);
    await getAll();
    notify && notify.success("Coach status updated", "Success");
  } catch (error) {
    console.error(error.message);
    notify && notify.error("Failed to update coach status", "Error");
  } finally {
    isLoading.value = false;
  }
};

const remove = async (id) => {
  isLoading.value = true;
  try {
    await coachService.delete(id);
    showModalDelete.value = false;
    await getAll();
    notify && notify.success("Coach deleted successfully", "Success");
  } catch (error) {
    console.error(error.message);
    notify && notify.error("Failed to delete coach", "Error");
  } finally {
    isLoading.value = false;
  }
};

const openModal = (id) => {
  itemId.value = id; // Set itemId
  showModalDelete.value = true;
};
</script>

<style>
/* General button styling */
.button-coach {
  display: inline-block;
  padding: 5px 20px;
  margin: 10px 0;
  border: 2px solid transparent;
  border-radius: 4px;
  font-size: var(--fs-medium);
  text-align: center;
  cursor: pointer;
  text-decoration: none;
}

/* Accepted button styling */
.button-coach-accepted {
  color: #002d72;
  border-color: #002d72;
}

/* Pending button styling */
.button-coach-pending {
  color: #000000;
  border-color: #000000;
}

/* Rejected button styling */
.button-coach-rejected {
  color: #d0021b;
  border-color: #d0021b;
}
</style>
